// SEO content for pillar pages, test detail pages, health-package pages and locality pages.
// All content is factual and conservative — no fabricated stats, prices, doctors or credentials.
// Prices are intentionally NOT hard-coded; they are fetched from the /api/tests + /api/packages CMS.

export const PILLARS = {
  "pathology-lab-ranchi": {
    slug: "pathology-lab-ranchi",
    title: "Pathology Lab in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Looking for a pathology lab in Ranchi? DoctorLab & Diagnostics provides pathology investigations, blood tests, health screening and sample collection services in Ranchi.",
    h1: "Pathology Lab in Ranchi",
    intro:
      "DoctorLab & Diagnostics is a pathology laboratory in Ranchi offering a wide range of investigations for routine health monitoring, preventive screening and doctor-advised testing. Our Lalpur laboratory serves patients from across Ranchi with sample collection at the centre or from home in eligible localities.",
    sections: [
      {
        h2: "What is a pathology laboratory?",
        body:
          "A pathology laboratory is a facility where blood, urine and other samples are tested to help doctors understand your health. Tests can detect infections, monitor chronic conditions like diabetes or thyroid disorders, check organ function, and support preventive screening. A dependable local pathology lab makes it easier for you to complete doctor-advised tests on time and share the reports quickly with your treating physician.",
      },
      {
        h2: "Pathology testing at DoctorLab",
        body:
          "At DoctorLab, samples are handled in a systematic laboratory workflow — collection, labelling, processing and reporting. Every sample is linked to the patient record at the point of collection to reduce the chance of mix-ups. Reports are shared through our digital reporting system so that you and your doctor have the information you need to plan next steps.",
      },
      {
        h2: "Blood tests in Ranchi",
        body:
          "We offer commonly-requested blood investigations including complete blood count (CBC), blood sugar, HbA1c, lipid profile, liver function tests (LFT), kidney function tests (KFT), thyroid profile, vitamin D and B12, iron studies and hormone tests. Explore the individual test pages for information on what each test measures, whether fasting is required and how to book.",
        links: [
          { to: "/tests/cbc-test-ranchi", label: "CBC test in Ranchi" },
          { to: "/tests/hba1c-test-ranchi", label: "HbA1c test in Ranchi" },
          { to: "/tests/lipid-profile-ranchi", label: "Lipid profile in Ranchi" },
          { to: "/tests/thyroid-test-ranchi", label: "Thyroid test in Ranchi" },
          { to: "/tests/liver-function-test-ranchi", label: "Liver function test" },
          { to: "/tests/kidney-function-test-ranchi", label: "Kidney function test" },
          { to: "/blood-test-ranchi", label: "See all blood tests" },
        ],
      },
      {
        h2: "Routine health screening",
        body:
          "Preventive testing lets you catch shifts in your health before symptoms appear. Depending on age, family history and lifestyle, doctors may suggest checks for blood sugar, cholesterol, thyroid, vitamin D, kidney and liver function. If you are unsure which tests to consider, please consult your physician — we can then help you complete the exact investigations your doctor advises.",
      },
      {
        h2: "Home sample collection",
        body:
          "Patients in select Ranchi localities can request home sample collection from DoctorLab. A trained collection team visits your home at a scheduled time, follows appropriate procedures, and transports the sample to the laboratory for processing. Home collection is available in the areas listed on the home collection page — please confirm availability for your locality while booking.",
        links: [{ to: "/home-sample-collection-ranchi", label: "Request home sample collection in Ranchi" }],
      },
      {
        h2: "How to book a test",
        body:
          "You can book a laboratory test with DoctorLab in three ways: call our helpline, WhatsApp us, or use the online booking form on this website. When booking, please have your doctor's prescription handy (if any) and share the exact test name, your preferred date and, for home collection, your locality in Ranchi.",
        links: [
          { to: "/book-test", label: "Book a test online" },
          { to: "/contact", label: "Contact DoctorLab" },
        ],
      },
      {
        h2: "Preparing for blood tests",
        body:
          "Some tests need fasting (usually 8–12 hours) — for example fasting blood sugar and lipid profile. Others, like CBC and thyroid, generally do not require fasting. Please carry your prescription, any previous reports for reference, and a valid ID. If you are on medication, ask your doctor whether to continue or pause on the day of the test.",
      },
      {
        h2: "Getting your laboratory report",
        body:
          "Reports are shared through our digital reporting system. The exact turnaround depends on the test — routine investigations are typically ready sooner than specialised ones. If you need help downloading or interpreting your report, please contact DoctorLab and we will guide you. For clinical interpretation, always share the report with your treating doctor.",
      },
      {
        h2: "Why choose a local diagnostic laboratory",
        body:
          "A neighbourhood pathology laboratory reduces travel, makes repeat testing easier, and lets you build a relationship with a team that already knows your history. DoctorLab is a local Ranchi laboratory — you get patient-friendly service, transparent information about the tests you are booking, and a single point of contact for both walk-in and home collection.",
      },
    ],
    faqs: [
      { q: "Where is the pathology lab in Ranchi located?", a: "DoctorLab & Diagnostics is located at Annapurna Chowk, Lalpur, Ranchi – 834001." },
      { q: "Do I need a doctor's prescription to book a blood test?", a: "A prescription is not mandatory for most laboratory tests, but we always recommend consulting your doctor before choosing tests, especially for a full checkup." },
      { q: "Do you provide home sample collection?", a: "Yes, home sample collection is available in select Ranchi localities. Please confirm availability for your locality at the time of booking." },
      { q: "How do I receive my report?", a: "Reports are shared through our digital reporting system. You can also collect a printed copy from the Lalpur lab." },
      { q: "What are your working hours?", a: "DoctorLab is open Monday to Sunday, 7:30 AM to 9:00 PM." },
    ],
    ctas: ["book", "call", "whatsapp", "directions"],
  },

  "diagnostic-centre-ranchi": {
    slug: "diagnostic-centre-ranchi",
    title: "Diagnostic Centre in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "DoctorLab & Diagnostics is a diagnostic centre in Ranchi focused on laboratory diagnostics — blood tests, pathology investigations, health checkups and home sample collection.",
    h1: "Diagnostic Centre in Ranchi",
    intro:
      "DoctorLab & Diagnostics is a Ranchi-based diagnostic centre specialising in laboratory investigations. Our focus is pathology — blood, urine and other laboratory tests — supported by a patient-friendly booking, collection and reporting process.",
    sections: [
      {
        h2: "What we do",
        body:
          "We are a laboratory-focused diagnostic centre. Our services cover routine pathology tests, preventive health checkups and specialised laboratory investigations that your doctor may advise. Sample collection is available at the Lalpur laboratory or from home in eligible localities.",
      },
      {
        h2: "What we do not do",
        body:
          "This website only lists services we genuinely offer. Radiology and imaging services (ultrasound, X-ray, CT scan and MRI) are not part of our current service list. If your doctor has advised imaging, please arrange it at a dedicated imaging centre.",
      },
      {
        h2: "Laboratory investigations available",
        body:
          "Our test menu covers commonly-ordered investigations — CBC, blood sugar, HbA1c, lipid profile, LFT, KFT, thyroid profile, urine routine, vitamin D, vitamin B12, iron studies, hormone tests and more. Speak to our team while booking if you have a specialised test on your prescription so we can confirm availability.",
        links: [
          { to: "/tests", label: "Browse all tests" },
          { to: "/blood-test-ranchi", label: "Blood tests in Ranchi" },
          { to: "/health-checkup-ranchi", label: "Health checkups" },
        ],
      },
      {
        h2: "Booking, collection and reporting",
        body:
          "Booking is done through the website, phone or WhatsApp. You may walk in to the Lalpur laboratory or request home collection in an eligible locality. Reports are shared through our digital reporting system.",
      },
      {
        h2: "Location and contact",
        body:
          "DoctorLab & Diagnostics is located at Annapurna Chowk, Lalpur, Ranchi – 834001. See the contact page for the phone number, WhatsApp, email and directions.",
        links: [{ to: "/contact", label: "Contact DoctorLab" }],
      },
    ],
    faqs: [
      { q: "Is DoctorLab a full diagnostic centre with imaging?", a: "No. Our current services focus on pathology / laboratory testing. We do not offer radiology or imaging." },
      { q: "Which laboratory tests do you offer?", a: "We cover common pathology investigations including CBC, blood sugar, HbA1c, lipid profile, LFT, KFT, thyroid profile, urine tests and more. Please check the individual test pages for details." },
      { q: "Where is the diagnostic centre located in Ranchi?", a: "At Annapurna Chowk, Lalpur, Ranchi – 834001." },
    ],
    ctas: ["book", "call", "directions"],
  },

  "blood-test-ranchi": {
    slug: "blood-test-ranchi",
    title: "Blood Test in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Book a blood test in Ranchi at DoctorLab & Diagnostics — CBC, blood sugar, HbA1c, lipid profile, LFT, KFT, thyroid, vitamin B12/D and more. Home collection available in select localities.",
    h1: "Blood Test in Ranchi",
    intro:
      "DoctorLab & Diagnostics is a Ranchi-based pathology laboratory offering commonly requested blood tests, from a simple complete blood count (CBC) to specialised investigations advised by your doctor. Samples can be collected at our Lalpur centre or from home in eligible localities.",
    sections: [
      {
        h2: "Common blood tests we offer",
        body:
          "We provide the routine pathology investigations most patients need for check-ups or monitoring. Explore the individual test pages for details on what each test measures, whether fasting is required, and typical use cases.",
        links: [
          { to: "/tests/cbc-test-ranchi", label: "CBC (Complete Blood Count)" },
          { to: "/tests/blood-sugar-test-ranchi", label: "Blood sugar test (FBS / PPBS)" },
          { to: "/tests/hba1c-test-ranchi", label: "HbA1c" },
          { to: "/tests/lipid-profile-ranchi", label: "Lipid profile" },
          { to: "/tests/liver-function-test-ranchi", label: "Liver function test (LFT)" },
          { to: "/tests/kidney-function-test-ranchi", label: "Kidney function test (KFT)" },
          { to: "/tests/thyroid-test-ranchi", label: "Thyroid profile (T3, T4, TSH)" },
          { to: "/tests/vitamin-b12-test-ranchi", label: "Vitamin B12" },
          { to: "/tests/vitamin-d-test-ranchi", label: "Vitamin D" },
          { to: "/tests/iron-profile-ranchi", label: "Iron profile" },
          { to: "/tests/hormone-tests-ranchi", label: "Hormone tests" },
        ],
      },
      {
        h2: "How to book a blood test",
        body:
          "Book online through this website, call DoctorLab or WhatsApp us. Share the exact test name from your prescription. We will confirm the test details and, if you have chosen home collection, the visit slot for your locality.",
      },
      {
        h2: "When fasting is required",
        body:
          "Fasting is typically required for fasting blood sugar, lipid profile, and some hormone tests. For those, please do not eat or drink anything except water for 8–12 hours before sample collection. If unsure, ask us while booking — we will confirm the fasting requirement for your specific test.",
      },
      {
        h2: "How sample collection works",
        body:
          "At the Lalpur laboratory, walk in during working hours; our team will collect the sample after verifying your details. If you have requested home collection, a trained team member will visit at the scheduled time, collect the sample following standard procedures, and transport it to the laboratory for processing.",
      },
      {
        h2: "Home collection for blood tests",
        body:
          "Home collection is available in select Ranchi localities. Please share your address and preferred date/time while booking so we can confirm availability. See the home collection page for the current list of covered localities.",
        links: [{ to: "/home-sample-collection-ranchi", label: "Home sample collection in Ranchi" }],
      },
      {
        h2: "How reports are delivered",
        body:
          "Reports are shared through our digital reporting system. Turnaround time varies by test. If you need help accessing your report, please call the DoctorLab helpline.",
      },
    ],
    faqs: [
      { q: "Which blood test do I need?", a: "Your doctor is the best person to advise which tests you need. DoctorLab helps you complete the tests on your prescription — we do not prescribe tests or offer medical advice." },
      { q: "Do you provide fasting blood sugar and lipid profile testing?", a: "Yes. Please fast for 8–12 hours before the sample is collected. Water is allowed." },
      { q: "How soon will I get my report?", a: "Turnaround depends on the test. Please check the individual test page or ask us while booking." },
    ],
    ctas: ["book", "call", "whatsapp"],
  },

  "home-sample-collection-ranchi": {
    slug: "home-sample-collection-ranchi",
    title: "Home Sample Collection in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Request home blood sample collection in Ranchi from DoctorLab & Diagnostics. Trained collection team, standard procedures, digital report delivery. Available in select localities.",
    h1: "Home Sample Collection in Ranchi",
    intro:
      "DoctorLab offers home sample collection for eligible pathology tests in select Ranchi localities. A trained team visits at a scheduled time, follows appropriate collection procedures, and transports the sample to our Lalpur laboratory for processing.",
    sections: [
      {
        h2: "How home collection works",
        body:
          "1. Book online, call or WhatsApp with the tests you need. 2. Share your address and preferred slot. 3. We confirm availability for your locality. 4. Our team visits at the scheduled time. 5. Sample is collected following standard procedures and transported to the laboratory. 6. You receive your report through our digital reporting system.",
      },
      {
        h2: "Ranchi areas served",
        body:
          "Home collection is currently available in these Ranchi localities. If your locality is not listed, please still call us — we may be able to accommodate the visit or suggest the nearest walk-in slot at the Lalpur lab.",
        links: [
          { to: "/locations/lalpur-ranchi", label: "Lalpur" },
          { to: "/locations/bariatu-ranchi", label: "Bariatu" },
          { to: "/locations/harmu-ranchi", label: "Harmu" },
          { to: "/locations/kanke-ranchi", label: "Kanke" },
          { to: "/locations/morabadi-ranchi", label: "Morabadi" },
          { to: "/locations/doranda-ranchi", label: "Doranda" },
          { to: "/locations/hinoo-ranchi", label: "Hinoo" },
          { to: "/locations/kokar-ranchi", label: "Kokar" },
          { to: "/locations/ratu-ranchi", label: "Ratu" },
          { to: "/locations/booty-more-ranchi", label: "Booty More" },
        ],
      },
      {
        h2: "How to book",
        body:
          "The fastest way to book a home visit is through the online form on this page or by WhatsApp. Please have your prescription (if any) ready and share the exact test name(s), your locality and a preferred time.",
        links: [{ to: "/book-test", label: "Book a home visit" }],
      },
      {
        h2: "Fasting instructions",
        body:
          "For tests that need fasting (typically 8–12 hours) such as fasting blood sugar and lipid profile, please schedule the visit for the morning and avoid food or drinks except water. If you are unsure whether your test requires fasting, ask us while booking.",
      },
      {
        h2: "What to keep ready",
        body:
          "Please keep your doctor's prescription (if any), any previous reports for reference, and a valid ID. Wear a comfortable half-sleeve or loose-sleeve outfit so that the collection team can access the arm easily.",
      },
      {
        h2: "Report delivery",
        body:
          "Reports are shared through our digital reporting system. Timelines vary by test. If a physical copy is needed, you may collect it from the Lalpur laboratory.",
      },
    ],
    faqs: [
      { q: "Is home collection available in my locality?", a: "Home collection is available in select Ranchi localities. Please call or WhatsApp to confirm availability for your address." },
      { q: "Is there a charge for home collection?", a: "Home collection charges (if any) vary by test and locality. Please confirm the applicable amount while booking." },
      { q: "Which tests can be collected at home?", a: "Most routine blood and urine tests can be collected at home. Some specialised tests may require you to visit the laboratory — we will let you know while confirming your booking." },
      { q: "How do I identify the DoctorLab team member?", a: "Our team member will call or message you before arrival. Please ask for their name and identification when they reach your address." },
    ],
    ctas: ["book", "call", "whatsapp"],
  },

  "health-checkup-ranchi": {
    slug: "health-checkup-ranchi",
    title: "Health Checkup in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Book a preventive health checkup in Ranchi with DoctorLab & Diagnostics. Full body checkups, diabetes screening, women's health and senior citizen packages.",
    h1: "Health Checkup in Ranchi",
    intro:
      "Preventive health checkups help you track key markers over time and catch shifts before symptoms appear. DoctorLab & Diagnostics offers curated health checkup packages in Ranchi for adults, people managing diabetes, women's health and senior citizens.",
    sections: [
      {
        h2: "Why choose a preventive health checkup",
        body:
          "A yearly or half-yearly checkup gives you a baseline for common health markers — blood sugar, cholesterol, thyroid, vitamin D and B12, liver and kidney function. Reviewing the trend with your doctor helps you make small, timely changes to diet, exercise or medication.",
      },
      {
        h2: "Health checkup packages",
        body:
          "Choose a package that fits your age, lifestyle and concerns. Prices and included tests are managed through our CMS and shown on each package page.",
        links: [
          { to: "/health-packages/full-body-checkup-ranchi", label: "Full body checkup" },
          { to: "/health-packages/diabetes-health-checkup-ranchi", label: "Diabetes health checkup" },
          { to: "/health-packages/womens-health-checkup-ranchi", label: "Women's health checkup" },
          { to: "/health-packages/senior-citizen-health-checkup-ranchi", label: "Senior citizen checkup" },
        ],
      },
      {
        h2: "How to prepare",
        body:
          "Most preventive checkups include fasting tests (blood sugar, lipid profile). Fast for 8–12 hours before the sample is collected — water is allowed. Carry your prescription (if any) and any previous reports.",
      },
      {
        h2: "Home collection for checkups",
        body:
          "You can request home collection for eligible checkup packages in select Ranchi localities. Please share your address and preferred time while booking.",
      },
      {
        h2: "Talk to your doctor",
        body:
          "A health checkup is a screening tool, not a diagnosis. Please share the report with your treating doctor to interpret the results and plan follow-up if needed.",
      },
    ],
    faqs: [
      { q: "Is a doctor's prescription needed for a health checkup?", a: "A prescription is generally not required for a preventive checkup package. However, we recommend consulting your doctor if you have specific concerns." },
      { q: "How long does a checkup take?", a: "Sample collection itself is quick (usually 10–15 minutes). The report is shared through our digital reporting system once processing is complete." },
      { q: "Can I do a checkup at home?", a: "Yes, home collection is available for eligible packages in select Ranchi localities. Please confirm while booking." },
    ],
    ctas: ["book", "call"],
  },
};

// ------------------------ TEST DETAIL PAGES ------------------------
// Slug pattern: /tests/<slug>
export const TESTS_SEO = [
  {
    slug: "cbc-test-ranchi",
    linkedTestId: "cbc",
    testName: "Complete Blood Count (CBC)",
    title: "CBC Test in Ranchi | Complete Blood Count | DoctorLab",
    metaDescription:
      "Book a CBC test in Ranchi at DoctorLab & Diagnostics. Learn what a complete blood count measures, whether fasting is required, and how to request home collection.",
    h1: "CBC Test in Ranchi – Complete Blood Count",
    intro:
      "A Complete Blood Count (CBC) is one of the most commonly requested blood tests. DoctorLab & Diagnostics offers CBC testing in Ranchi at our Lalpur laboratory, with home sample collection available in select localities.",
    what: "A CBC measures the different types of cells in your blood — red blood cells (RBCs), white blood cells (WBCs) and platelets, along with related indices such as haemoglobin and haematocrit.",
    why: "Doctors commonly recommend CBC as part of a routine health checkup, when investigating symptoms like fatigue or weakness, to check for infection or inflammation, and to monitor known conditions.",
    parameters: [
      "Haemoglobin (Hb)", "Red Blood Cell (RBC) count", "White Blood Cell (WBC) count",
      "Platelet count", "Haematocrit (PCV)", "MCV, MCH, MCHC", "Differential leucocyte count",
    ],
    fasting: "No — fasting is generally not required for a CBC.",
    sample: "A small blood sample is drawn from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "hba1c-test-ranchi",
    linkedTestId: "hba1c",
    testName: "HbA1c",
    title: "HbA1c Test in Ranchi | Glycated Haemoglobin | DoctorLab",
    metaDescription:
      "HbA1c test in Ranchi at DoctorLab & Diagnostics. Understand what HbA1c measures, how it is used to monitor blood sugar over 2–3 months, and how to book.",
    h1: "HbA1c Test in Ranchi",
    intro:
      "The HbA1c (glycated haemoglobin) test gives an estimate of your average blood sugar over the past 2–3 months. DoctorLab offers HbA1c testing in Ranchi with the option of home sample collection in select localities.",
    what: "HbA1c reflects the percentage of haemoglobin in the blood that has attached sugar — the higher the average blood sugar, the higher the HbA1c.",
    why: "Doctors commonly use HbA1c to screen for and monitor diabetes and prediabetes.",
    parameters: ["HbA1c (%)", "Estimated average glucose (optional)"],
    fasting: "No — HbA1c does not require fasting.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "blood-sugar-test-ranchi",
    linkedTestId: "fbs",
    testName: "Blood Sugar Test (FBS / PPBS)",
    title: "Blood Sugar Test in Ranchi | FBS & PPBS | DoctorLab",
    metaDescription:
      "Book a blood sugar test in Ranchi (fasting or post-meal) at DoctorLab & Diagnostics. Learn how to prepare, when doctors recommend the test, and how to book.",
    h1: "Blood Sugar Test in Ranchi",
    intro:
      "Blood sugar tests — fasting (FBS) and post-prandial (PPBS) — help detect and monitor diabetes and prediabetes. DoctorLab offers both tests in Ranchi with the option of home collection in eligible localities.",
    what: "FBS measures blood glucose after 8 hours of fasting. PPBS measures blood glucose 2 hours after a meal.",
    why: "Doctors use these tests to screen for or monitor diabetes, and to review the effect of diet or medication on blood sugar.",
    parameters: ["Fasting blood glucose", "Post-prandial (2-hour) blood glucose"],
    fasting: "Yes for FBS (8–12 hours). PPBS is done 2 hours after a normal meal.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "lipid-profile-ranchi",
    linkedTestId: "lipid",
    testName: "Lipid Profile",
    title: "Lipid Profile Test in Ranchi | Cholesterol | DoctorLab",
    metaDescription:
      "Lipid profile test in Ranchi at DoctorLab & Diagnostics — total cholesterol, HDL, LDL, VLDL and triglycerides. Fasting instructions and booking information.",
    h1: "Lipid Profile Test in Ranchi",
    intro:
      "A lipid profile measures the fats (lipids) in your blood — key markers used to assess cardiovascular risk. DoctorLab offers lipid profile testing in Ranchi with home collection available in select localities.",
    what: "The panel typically includes total cholesterol, HDL, LDL, VLDL and triglycerides. Some panels also report cholesterol ratios.",
    why: "Doctors order a lipid profile as part of a heart-health check, when reviewing family history of cardiovascular disease, and to monitor the effect of lifestyle changes or medication.",
    parameters: ["Total cholesterol", "HDL cholesterol", "LDL cholesterol", "VLDL", "Triglycerides", "Cholesterol : HDL ratio"],
    fasting: "Generally 9–12 hours of fasting is recommended. Please confirm while booking.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "liver-function-test-ranchi",
    linkedTestId: "lft",
    testName: "Liver Function Test (LFT)",
    title: "Liver Function Test (LFT) in Ranchi | DoctorLab",
    metaDescription:
      "Liver function test (LFT) in Ranchi at DoctorLab & Diagnostics. Understand what a liver panel measures and how to book at the Lalpur lab or from home.",
    h1: "Liver Function Test (LFT) in Ranchi",
    intro:
      "A Liver Function Test (LFT) is a panel of blood investigations that gives an overview of liver health. DoctorLab offers LFT testing in Ranchi.",
    what: "The panel typically includes SGOT/AST, SGPT/ALT, alkaline phosphatase (ALP), total bilirubin (direct + indirect), total protein and albumin/globulin.",
    why: "Doctors order LFT to investigate symptoms such as jaundice, fatigue or abdominal discomfort, to monitor known liver conditions, or before starting certain medications.",
    parameters: ["SGOT (AST)", "SGPT (ALT)", "Alkaline phosphatase", "Total bilirubin (direct & indirect)", "Total protein", "Albumin / Globulin ratio"],
    fasting: "Fasting is not always required. Please confirm while booking.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "kidney-function-test-ranchi",
    linkedTestId: "kft",
    testName: "Kidney Function Test (KFT)",
    title: "Kidney Function Test (KFT) in Ranchi | DoctorLab",
    metaDescription:
      "Book a kidney function test (KFT) in Ranchi at DoctorLab & Diagnostics. Panel typically includes urea, creatinine, uric acid and electrolytes.",
    h1: "Kidney Function Test (KFT) in Ranchi",
    intro:
      "A Kidney Function Test (KFT) reviews how well your kidneys are working. DoctorLab offers KFT testing in Ranchi.",
    what: "The panel typically includes blood urea, serum creatinine, uric acid, and electrolytes such as sodium, potassium and chloride.",
    why: "Doctors use KFT to investigate symptoms suggesting kidney trouble, to monitor chronic conditions such as diabetes and hypertension that can affect the kidneys, and before some medications.",
    parameters: ["Blood urea", "Serum creatinine", "Uric acid", "Sodium (Na+)", "Potassium (K+)", "Chloride (Cl-)"],
    fasting: "Fasting is generally not required. Please confirm while booking.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "thyroid-test-ranchi",
    linkedTestId: "tsh",
    testName: "Thyroid Profile (T3, T4, TSH)",
    title: "Thyroid Test in Ranchi | T3, T4, TSH | DoctorLab",
    metaDescription:
      "Thyroid profile test in Ranchi (T3, T4, TSH) at DoctorLab & Diagnostics. Book at the Lalpur lab or from home in eligible localities.",
    h1: "Thyroid Test in Ranchi (T3, T4, TSH)",
    intro:
      "A thyroid profile helps evaluate how the thyroid gland is functioning. DoctorLab offers the T3, T4 and TSH panel in Ranchi.",
    what: "The basic panel measures Triiodothyronine (T3), Thyroxine (T4) and Thyroid-Stimulating Hormone (TSH). Free T3 and Free T4 can be added if requested.",
    why: "Doctors order thyroid tests to investigate symptoms like unexplained weight change, fatigue, mood change, palpitations, or menstrual irregularities, and to monitor patients on thyroid medication.",
    parameters: ["T3 (Total Triiodothyronine)", "T4 (Total Thyroxine)", "TSH (Thyroid-Stimulating Hormone)"],
    fasting: "Fasting is not usually required. If you are on thyroid medication, please follow your doctor's advice.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "urine-test-ranchi",
    linkedTestId: "urine",
    testName: "Urine Routine",
    title: "Urine Test in Ranchi | Urine Routine & Microscopy | DoctorLab",
    metaDescription:
      "Urine routine and microscopy test in Ranchi at DoctorLab & Diagnostics. Learn what a urine test checks and how to collect the sample.",
    h1: "Urine Test in Ranchi",
    intro:
      "A urine routine and microscopy test looks at physical, chemical and microscopic characteristics of urine. DoctorLab offers urine testing in Ranchi.",
    what: "The report typically covers colour, appearance, pH, specific gravity, protein, glucose, ketones, bilirubin, urobilinogen, blood, RBCs, WBCs, casts and crystals.",
    why: "Doctors use urine routine tests as part of a routine checkup, to investigate urinary symptoms, and to monitor conditions such as diabetes.",
    parameters: ["Colour and appearance", "pH & specific gravity", "Protein, glucose, ketones", "RBCs, WBCs, casts, crystals"],
    fasting: "Fasting is not required.",
    sample: "A midstream urine sample collected in a clean container provided by the laboratory.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "vitamin-b12-test-ranchi",
    linkedTestId: "vitb12",
    testName: "Vitamin B12",
    title: "Vitamin B12 Test in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Vitamin B12 test in Ranchi at DoctorLab & Diagnostics. Learn when the test is commonly recommended and how to book.",
    h1: "Vitamin B12 Test in Ranchi",
    intro:
      "Vitamin B12 supports nerve function and red blood cell formation. DoctorLab offers vitamin B12 testing in Ranchi.",
    what: "The test measures the level of vitamin B12 in the blood.",
    why: "Doctors may recommend a B12 test for symptoms such as fatigue, tingling, memory changes, or when reviewing diet-related deficiencies.",
    parameters: ["Serum vitamin B12"],
    fasting: "Fasting is not usually required. Please confirm while booking.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "vitamin-d-test-ranchi",
    linkedTestId: "vitd",
    testName: "Vitamin D",
    title: "Vitamin D Test in Ranchi | 25-Hydroxy Vitamin D | DoctorLab",
    metaDescription:
      "Book a vitamin D (25-OH) test in Ranchi at DoctorLab & Diagnostics. Understand when doctors advise the test and how to book.",
    h1: "Vitamin D Test in Ranchi",
    intro:
      "Vitamin D helps the body absorb calcium and supports bone health. DoctorLab offers vitamin D (25-hydroxy) testing in Ranchi.",
    what: "The test measures 25-hydroxy vitamin D — the storage form of vitamin D.",
    why: "Doctors may recommend a vitamin D test for bone pain, muscle weakness, chronic fatigue, or when reviewing overall nutritional status.",
    parameters: ["25-Hydroxy vitamin D (Total)"],
    fasting: "Fasting is not required.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "iron-profile-ranchi",
    linkedTestId: "iron",
    testName: "Iron Profile",
    title: "Iron Profile Test in Ranchi | Ferritin & TIBC | DoctorLab",
    metaDescription:
      "Iron profile test in Ranchi at DoctorLab & Diagnostics — serum iron, ferritin and TIBC. Understand when doctors advise the test.",
    h1: "Iron Profile Test in Ranchi",
    intro:
      "An iron profile helps evaluate iron stores in the body and is commonly used in the work-up of anaemia. DoctorLab offers iron profile testing in Ranchi.",
    what: "The panel typically includes serum iron, ferritin and total iron-binding capacity (TIBC).",
    why: "Doctors may recommend an iron profile for symptoms of anaemia or as follow-up when routine blood tests suggest low iron.",
    parameters: ["Serum iron", "Ferritin", "Total iron-binding capacity (TIBC)"],
    fasting: "Sometimes advised depending on lab protocol. Please confirm while booking.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "hormone-tests-ranchi",
    linkedTestId: "hormone",
    testName: "Hormone Tests",
    title: "Hormone Tests in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Hormone tests in Ranchi at DoctorLab & Diagnostics. We support commonly requested hormone panels — please share your prescription for exact tests.",
    h1: "Hormone Tests in Ranchi",
    intro:
      "DoctorLab supports commonly requested hormone tests in Ranchi, from thyroid hormones to reproductive and metabolic hormones. Because hormone testing is highly personalised, please share your doctor's prescription so we can confirm the exact test and preparation.",
    what: "Hormone tests measure specific hormone levels in blood or urine, depending on what your doctor has advised.",
    why: "Doctors order hormone tests to investigate a wide range of concerns — thyroid disorders, menstrual issues, PCOD/PCOS work-up, fertility evaluation, growth concerns and more.",
    parameters: ["Thyroid hormones (T3, T4, TSH)", "Reproductive hormones (LH, FSH, prolactin, oestradiol, testosterone) — as advised", "Others as advised"],
    fasting: "Depends on the specific hormone panel. Please confirm while booking.",
    sample: "Blood sample from a vein in the arm; urine samples for specific tests.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "urine-culture-ranchi",
    linkedTestId: "urine-culture",
    testName: "Urine Culture",
    title: "Urine Culture Test in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Urine culture test in Ranchi at DoctorLab & Diagnostics. Understand when doctors advise a urine culture and how to collect the sample.",
    h1: "Urine Culture Test in Ranchi",
    intro:
      "A urine culture is used to identify bacteria that may be causing a urinary infection. DoctorLab offers urine culture testing in Ranchi.",
    what: "The sample is cultured in the laboratory to identify bacteria and, if positive, sensitivity to antibiotics is reported.",
    why: "Doctors order a urine culture when a urinary tract infection is suspected, or to guide antibiotic choice.",
    parameters: ["Bacterial growth", "Colony count", "Antibiotic sensitivity (if positive)"],
    fasting: "Not required. Please follow the clean-catch instructions provided by the laboratory.",
    sample: "A midstream urine sample in a sterile container provided by the laboratory.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "stool-test-ranchi",
    linkedTestId: "stool",
    testName: "Stool Test",
    title: "Stool Test in Ranchi | Stool Routine & Microscopy | DoctorLab",
    metaDescription:
      "Stool routine and microscopy test in Ranchi at DoctorLab & Diagnostics. Learn what the test checks and how to collect the sample.",
    h1: "Stool Test in Ranchi",
    intro:
      "A stool routine and microscopy test looks at physical and microscopic characteristics of the stool. DoctorLab offers stool testing in Ranchi.",
    what: "The report typically covers colour, consistency, presence of blood, mucus, ova and parasites.",
    why: "Doctors may advise a stool test for gastrointestinal symptoms, suspected infections, or to check for occult blood.",
    parameters: ["Physical characteristics", "Microscopic examination", "Occult blood (if requested)"],
    fasting: "Not required.",
    sample: "A stool sample collected in a container provided by the laboratory.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "microbiology-tests-ranchi",
    linkedTestId: "microbiology",
    testName: "Microbiology Tests",
    title: "Microbiology Tests in Ranchi | Cultures & Sensitivity | DoctorLab",
    metaDescription:
      "Microbiology tests in Ranchi at DoctorLab & Diagnostics — cultures and sensitivity from urine, stool, sputum and other samples as advised by your doctor.",
    h1: "Microbiology Tests in Ranchi",
    intro:
      "Microbiology tests identify microorganisms in samples such as urine, stool, sputum and swabs. DoctorLab supports commonly-requested microbiology investigations in Ranchi — please share your prescription for the exact test.",
    what: "The sample is cultured and, if organisms grow, they are identified and (where relevant) tested for antibiotic sensitivity.",
    why: "Doctors order microbiology tests when a bacterial or fungal infection is suspected and precise identification is helpful.",
    parameters: ["Culture (aerobic / other as advised)", "Organism identification", "Antibiotic sensitivity (where relevant)"],
    fasting: "Not required.",
    sample: "Depends on the specific test — urine, stool, sputum or swab. Instructions provided at the time of collection.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "serology-tests-ranchi",
    linkedTestId: "serology",
    testName: "Serology Tests",
    title: "Serology Tests in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Serology tests in Ranchi at DoctorLab & Diagnostics — commonly requested antibody / antigen investigations as advised by your doctor.",
    h1: "Serology Tests in Ranchi",
    intro:
      "Serology tests look at antibodies or antigens in the blood. DoctorLab offers commonly requested serology investigations in Ranchi.",
    what: "The specific parameters depend on the exact serology test your doctor has advised.",
    why: "Serology tests support the work-up of specific infections and immune conditions, as advised by a doctor.",
    parameters: ["Test-specific antibodies or antigens (as advised)"],
    fasting: "Depends on the test. Please confirm while booking.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
  {
    slug: "coagulation-tests-ranchi",
    linkedTestId: "coagulation",
    testName: "Coagulation Tests",
    title: "Coagulation Tests in Ranchi | PT, INR, aPTT | DoctorLab",
    metaDescription:
      "Coagulation tests in Ranchi at DoctorLab & Diagnostics — PT, INR, aPTT and other clotting studies as advised by your doctor.",
    h1: "Coagulation Tests in Ranchi",
    intro:
      "Coagulation tests assess how well your blood clots. DoctorLab offers commonly requested coagulation investigations in Ranchi.",
    what: "The specific parameters depend on the exact test advised — commonly PT, INR and aPTT.",
    why: "Doctors order coagulation tests to investigate bleeding or clotting issues, and to monitor patients on blood-thinning medication.",
    parameters: ["PT (Prothrombin Time)", "INR", "aPTT"],
    fasting: "Not usually required. Please confirm while booking.",
    sample: "A small blood sample from a vein in the arm.",
    reportTat: "Report turnaround time varies. Please confirm at the time of booking.",
  },
];

// ------------------------ HEALTH PACKAGE SEO PAGES ------------------------
export const PACKAGES_SEO = [
  {
    slug: "full-body-checkup-ranchi",
    linkedPackageId: "full-body-basic",
    title: "Full Body Checkup in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Book a full body checkup in Ranchi at DoctorLab & Diagnostics. A broad panel covering routine blood, sugar, cholesterol, thyroid, liver and kidney markers.",
    h1: "Full Body Checkup in Ranchi",
    intro:
      "A full body checkup gives a broad view of your health across common laboratory markers. DoctorLab offers full body checkup packages in Ranchi with the option of home collection in select localities.",
    goodFor: [
      "Adults who want a yearly preventive check",
      "People with a family history of common lifestyle conditions",
      "Individuals starting a new lifestyle or fitness plan",
    ],
    typicalIncludes: [
      "Complete blood count (CBC)",
      "Fasting blood sugar",
      "HbA1c",
      "Lipid profile",
      "Thyroid profile (T3, T4, TSH)",
      "Liver function test",
      "Kidney function test",
      "Urine routine",
    ],
    prep: "Fast for 8–12 hours before sample collection (water is allowed). Carry any previous reports and your prescription (if any).",
    reportInfo: "Reports are delivered through our digital reporting system. Please share the report with your doctor for interpretation.",
  },
  {
    slug: "diabetes-health-checkup-ranchi",
    linkedPackageId: "diabetes-care",
    title: "Diabetes Health Checkup in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Diabetes health checkup in Ranchi — fasting sugar, PPBS, HbA1c and related markers. Book at DoctorLab & Diagnostics.",
    h1: "Diabetes Health Checkup in Ranchi",
    intro:
      "The diabetes checkup panel focuses on markers relevant to blood sugar control. Ideal as a periodic review for people with diabetes or prediabetes, on their doctor's advice.",
    goodFor: [
      "People diagnosed with diabetes / prediabetes",
      "Adults with a family history of diabetes",
      "Individuals whose doctor has advised periodic diabetes monitoring",
    ],
    typicalIncludes: [
      "Fasting blood sugar (FBS)",
      "Post-prandial blood sugar (PPBS)",
      "HbA1c",
      "Urine routine",
      "Kidney function markers",
    ],
    prep: "Fast for 8–12 hours for FBS and HbA1c. PPBS is done 2 hours after a normal meal.",
    reportInfo: "Reports are delivered through our digital reporting system.",
  },
  {
    slug: "womens-health-checkup-ranchi",
    linkedPackageId: "women-wellness",
    title: "Women's Health Checkup in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Women's health checkup in Ranchi — routine blood, iron, thyroid and hormone-related markers as advised. Book at DoctorLab & Diagnostics.",
    h1: "Women's Health Checkup in Ranchi",
    intro:
      "A women's health checkup panel focuses on markers commonly reviewed for adult women, on their doctor's advice.",
    goodFor: [
      "Adult women who want a periodic health review",
      "Women being reviewed for common concerns such as fatigue or anaemia",
    ],
    typicalIncludes: [
      "Complete blood count (CBC)",
      "Fasting blood sugar",
      "Lipid profile",
      "Thyroid profile (T3, T4, TSH)",
      "Iron studies",
      "Vitamin B12 / Vitamin D (as advised)",
      "Urine routine",
    ],
    prep: "Fast for 8–12 hours for fasting tests. Carry any previous reports.",
    reportInfo: "Reports are delivered through our digital reporting system.",
  },
  {
    slug: "senior-citizen-health-checkup-ranchi",
    linkedPackageId: "senior-care",
    title: "Senior Citizen Health Checkup in Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Senior citizen health checkup in Ranchi at DoctorLab & Diagnostics — broader panel covering common age-related markers on the doctor's advice.",
    h1: "Senior Citizen Health Checkup in Ranchi",
    intro:
      "The senior citizen checkup covers a broader set of laboratory markers commonly reviewed in older adults, on their doctor's advice.",
    goodFor: [
      "Adults 60+ who want a periodic health review",
      "Older adults with common lifestyle conditions being monitored by a doctor",
    ],
    typicalIncludes: [
      "Complete blood count (CBC)",
      "Fasting blood sugar & HbA1c",
      "Lipid profile",
      "Thyroid profile (T3, T4, TSH)",
      "Liver function test",
      "Kidney function test",
      "Urine routine",
      "Vitamin B12 / Vitamin D (as advised)",
    ],
    prep: "Fast for 8–12 hours for fasting tests. Carry a list of current medications.",
    reportInfo: "Reports are delivered through our digital reporting system.",
  },
];

// ------------------------ LOCALITY (RANCHI) SEO PAGES ------------------------
// Only the localities the user has confirmed DoctorLab genuinely serves.
export const LOCALITIES_SEO = [
  {
    slug: "lalpur-ranchi",
    locality: "Lalpur",
    isMain: true,
    title: "Pathology Lab in Lalpur, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "DoctorLab & Diagnostics is a pathology lab in Lalpur, Ranchi (Annapurna Chowk) offering blood tests, health checkups and home sample collection.",
    h1: "Pathology Lab in Lalpur, Ranchi",
    localBlurb:
      "DoctorLab & Diagnostics is located at Annapurna Chowk, Lalpur — the main laboratory for our Ranchi operations. Patients from Lalpur and the surrounding localities can walk in during opening hours or request home sample collection.",
    landmarks: "Annapurna Chowk area, close to the main Lalpur commercial belt.",
  },
  {
    slug: "bariatu-ranchi",
    locality: "Bariatu",
    title: "Pathology Lab Near Bariatu, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Bariatu, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Bariatu, Ranchi",
    localBlurb:
      "Patients in Bariatu can access DoctorLab & Diagnostics either by visiting our main laboratory at Annapurna Chowk, Lalpur, or by booking home sample collection to their address in Bariatu.",
    landmarks: "RIMS area and surrounding Bariatu residential belt.",
  },
  {
    slug: "harmu-ranchi",
    locality: "Harmu",
    title: "Pathology Lab Near Harmu, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Harmu, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Harmu, Ranchi",
    localBlurb:
      "Patients in Harmu can visit our Lalpur main lab or request home sample collection in Harmu. Please share your address while booking so we can confirm the visit slot.",
    landmarks: "Harmu Road and surrounding residential blocks.",
  },
  {
    slug: "kanke-ranchi",
    locality: "Kanke",
    title: "Pathology Lab Near Kanke, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Kanke, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Kanke, Ranchi",
    localBlurb:
      "Patients in Kanke can visit the DoctorLab main lab at Lalpur or request home sample collection in Kanke. We will confirm the visit slot at the time of booking.",
    landmarks: "Kanke Road, RINPAS area and surrounding localities.",
  },
  {
    slug: "morabadi-ranchi",
    locality: "Morabadi",
    title: "Pathology Lab Near Morabadi, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Morabadi, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Morabadi, Ranchi",
    localBlurb:
      "Patients in Morabadi can walk in to our Lalpur laboratory during working hours or book home sample collection to their Morabadi address.",
    landmarks: "Morabadi Ground area and surrounding residential blocks.",
  },
  {
    slug: "doranda-ranchi",
    locality: "Doranda",
    title: "Pathology Lab Near Doranda, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Doranda, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Doranda, Ranchi",
    localBlurb:
      "Patients in Doranda can access DoctorLab either at our main laboratory at Lalpur or through home sample collection at your Doranda address.",
    landmarks: "Doranda main market area and surrounding residential belts.",
  },
  {
    slug: "hinoo-ranchi",
    locality: "Hinoo",
    title: "Pathology Lab Near Hinoo, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Hinoo, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Hinoo, Ranchi",
    localBlurb:
      "Patients in Hinoo can visit the Lalpur main laboratory or request home sample collection at their Hinoo address.",
    landmarks: "Hinoo main road and adjoining residential areas.",
  },
  {
    slug: "kokar-ranchi",
    locality: "Kokar",
    title: "Pathology Lab Near Kokar, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Kokar, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Kokar, Ranchi",
    localBlurb:
      "Patients in Kokar can visit our Lalpur lab or request home sample collection at their address in Kokar.",
    landmarks: "Kokar Industrial Area and surrounding residential belt.",
  },
  {
    slug: "ratu-ranchi",
    locality: "Ratu",
    title: "Pathology Lab Near Ratu, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Ratu / Ratu Road, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Ratu, Ranchi",
    localBlurb:
      "Patients in Ratu / Ratu Road can visit the Lalpur laboratory or request home sample collection at their address.",
    landmarks: "Ratu Road corridor and adjoining residential areas.",
  },
  {
    slug: "booty-more-ranchi",
    locality: "Booty More",
    title: "Pathology Lab Near Booty More, Ranchi | DoctorLab & Diagnostics",
    metaDescription:
      "Blood tests and pathology services for patients in Booty More, Ranchi. Home sample collection available. Main lab at Annapurna Chowk, Lalpur.",
    h1: "Pathology Lab Near Booty More, Ranchi",
    localBlurb:
      "Patients in Booty More can visit the Lalpur main laboratory or request home sample collection at their address.",
    landmarks: "Booty More junction and adjoining residential belt.",
  },
];

// Simple keyed lookups
export const testBySlug = (s) => TESTS_SEO.find((t) => t.slug === s);
export const packageBySlug = (s) => PACKAGES_SEO.find((p) => p.slug === s);
export const localityBySlug = (s) => LOCALITIES_SEO.find((l) => l.slug === s);
export const pillarBySlug = (s) => PILLARS[s];
